import type { Notification } from '@/modules/Customer/interface/Notification';
import Image from 'next/image';
import Link from 'next/link';
import { Notification as NotificationIcon } from '@eqi-investimentos/icons/web';
import { Button, Flex, Text } from '@eqi-investimentos/react';
import { useT } from '@/libs/i18n/utils/useT';
import { appConfig } from '@/config/app';
import { Drawer } from '@/components/modules/Drawer';
import { useGetNotifications } from '@/modules/Customer/http/client/useGetNotifications';
import { formatDateToPTBRDayMonth } from '@/utils/date';
import { IfRender, MapRender } from '@/utils/jsx';

export const NotificationsButton = () => {
  const t = useT();

  const { data = { notifications: [], today_new_quantity: 0 } } =
    useGetNotifications();

  if (!data.notifications) return null;

  return (
    <Drawer
      id="notification-modal"
      showIconButton={false}
      closeButtonProps={{ top: '5xl', right: '2xl' }}
      backButtonProps={{ buttonProps: { top: '3xl', left: '2xl' } }}
      contentProps={{ bgColor: 'background.primary' }}
      headerProps={{ px: '4.5rem', py: '3xl' }}
      bodyProps={{ px: '2xl' }}
      headerContent={
        <Text color="content.primary" size="2xl">
          {t('attributes.notifications')}
        </Text>
      }
      showCustomButton
      customButton={
        <Button
          position="relative"
          variant="naked"
          bgColor="transparency.light"
          borderRadius="50%"
          p="xs"
          aria-label="button-open-notifications-drawer"
        >
          <IfRender condition={data.today_new_quantity > 0}>
            <Text
              position="absolute"
              top={0}
              right={0}
              w="1rem"
              h="1rem"
              size="label.s"
              lineHeight="1rem"
              bg="background.brandMid"
              color="content.alwaysLight"
              rounded="full"
            >
              {data.today_new_quantity}
            </Text>
          </IfRender>
          <NotificationIcon color="content.brand" boxSize="1.5rem" />
        </Button>
      }
    >
      <Flex direction="column" gap="3xl">
        <Flex direction="column" gap="l">
          <Flex flexDir="column" gap="xl">
            <MapRender
              items={data.notifications}
              render={(notification) => (
                <Flex
                  key={notification.title}
                  as={Link}
                  href={mountPathToLink(notification)}
                  justifyContent="center"
                  alignItems="center"
                  p="s"
                  pb="2.25rem"
                  gap="m"
                  w="full"
                  borderBottomWidth="1px"
                  borderColor="border.secondary"
                >
                  <Flex direction="column" gap="s">
                    <Text size="l" fontWeight="medium" color="content.brand">
                      {notification.title}
                    </Text>
                    <Text size="l" color="content.secondary" noOfLines={2}>
                      {notification.description}
                    </Text>
                    <Text size="m" color="content.tertiary">
                      {formatDateToPTBRDayMonth(
                        new Date(notification.send_after)
                      ).replace(' ', ' de ')}
                    </Text>
                  </Flex>
                  <Flex position="relative" minW="5.5rem" h="3.25rem" flex={1}>
                    <Image
                      src={getThumbNotification(notification?.thumb)}
                      alt={notification?.title}
                      fill
                      style={{
                        objectFit: 'cover',
                        borderRadius: '0.25rem',
                      }}
                      onError={({ currentTarget }) => {
                        currentTarget.onerror = null;
                        currentTarget.src = '/assets/eqi-placeholder.png';
                      }}
                    />
                  </Flex>
                </Flex>
              )}
            />
          </Flex>
        </Flex>
      </Flex>
    </Drawer>
  );
};

const contentTypeByURLType = {
  content: 'conteudo',
  contents: 'conteudo',
  default: 'conteudo',
  interview: 'conteudo',
  movie: 'conteudo',
  video: 'conteudo',
  event: 'eventos',
  events: 'eventos',
  course: 'programa',
  journey: 'programa',
  podcast: 'programa',
  program: 'programa',
  programs: 'programa',
  recommendation: 'programa',
  series: 'programa',
  tutorial: 'programa',
  analyst: 'autor',
  author: 'autor',
  autor: 'autor',
  journeyhome: 'jornadas',
  coursehome: 'cursos',
  entertainmenthome: 'filmes',
  research: 'research',
  investing: 'investir',
  news: 'noticias',
  newshome: 'noticias',
  recommended: 'ativos-recomendados',
  profile: 'meu-perfil',
  user: 'meu-usuario',
  historicuserciew: 'meu-historico',
  historicuserview: 'meu-historico',
  lives: 'ao-vivo',
  wallet: 'minha-carteira',
  businesscredit: 'credito-empresarial',
  exchange: 'cambio',
  freeenegymarket: 'mercado-livre-energia',
  homeequity: 'home-equity',
  lifeinsurance: 'seguro-vida',
  realstatefinancing: 'financiamento-imobiliario',
  consortium: 'consorcio',
  pension: 'previdencia',
  creditcard: 'cartao-de-credito',
} as const;

const mountPathToLink = (data?: Partial<Notification>) => {
  const typeContent =
    contentTypeByURLType[data?.url_type as ContentTypeByURLType] ||
    contentTypeByURLType.default;

  if (!data?.id && !data?.slug) {
    return '#';
  }

  if (typeContent === 'eventos') {
    return `/eventos`;
  }

  if (data?.slug?.includes(appConfig.scheme)) {
    return data.slug.split(appConfig.scheme)[1];
  }

  const urlRedirect = (data?.slug || data?.id) ?? '';
  return `/${typeContent}/${urlRedirect}`;
};

const getThumbNotification = (thumb: string) => {
  if (!thumb) return '/assets/placeholder_assets.png';

  return thumb?.startsWith('http') ? thumb : '/assets/placeholder_assets.png';
};

type ContentTypeByURLType = keyof typeof contentTypeByURLType;
